/**
 * Embudo Lean Startup — área apilada 100% (D7 → W10).
 * Compara cohorte de las últimas 10 semanas vs las 10 anteriores.
 */

import { useEffect, useMemo, useState } from 'react';
import { cn } from '@/lib/utils';
import { RefreshCw, Info, TrendingUp, TrendingDown } from 'lucide-react';
import {
    AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend,
} from 'recharts';
import {
    useProductLeanFunnel,
    lastComparableCheckpoint,
    type LeanFunnelStageKey,
    type LeanFunnelCheckpoint,
} from '@/hooks/useProductLeanFunnel';

const STAGE_COLORS: Record<LeanFunnelStageKey, string> = {
    no_session: '#94a3b8',
    connected: '#38bdf8',
    onboarding: '#a78bfa',
    first_capture: '#34d399',
    retained: '#fbbf24',
    still_active: '#f97316',
    pro: '#f43f5e',
};

const STAGE_FILL_OPACITY: Record<LeanFunnelStageKey, number> = {
    no_session: 0.28,
    connected: 0.85,
    onboarding: 0.85,
    first_capture: 0.85,
    retained: 0.85,
    still_active: 0.85,
    pro: 0.9,
};

/** Orden de apilado visual: arriba = menos valor, abajo = más valor (como Lean Startup). */
const STACK_TOP_TO_BOTTOM: LeanFunnelStageKey[] = [
    'no_session',
    'connected',
    'onboarding',
    'first_capture',
    'retained',
    'still_active',
    'pro',
];

type SeriesMode = 'current' | 'previous';

function toChartRows(points: LeanFunnelCheckpoint[]) {
    return points.map((p) => ({
        label: p.label,
        key: p.key,
        cohort_n: p.cohort_n,
        ...p.stages,
    }));
}

function StageDelta({
    current,
    previous,
    stage,
    checkpointLabel,
}: {
    current: LeanFunnelCheckpoint[];
    previous: LeanFunnelCheckpoint[];
    stage: LeanFunnelStageKey;
    checkpointLabel: string;
}) {
    const comparable = lastComparableCheckpoint(current, previous);
    if (!comparable) return null;
    const cur = current.find((c) => c.key === comparable.key);
    const prev = previous.find((c) => c.key === comparable.key);
    if (!cur || !prev) return null;
    const delta = Number((cur.stages[stage] - prev.stages[stage]).toFixed(1));
    if (!Number.isFinite(delta)) return null;
    const positiveIsGood = stage !== 'no_session';
    const good = positiveIsGood ? delta >= 0 : delta <= 0;
    return (
        <span
            className={cn(
                'inline-flex items-center gap-0.5 text-[11px] font-semibold',
                good ? 'text-emerald-400' : 'text-rose-400'
            )}
        >
            {delta >= 0 ? <TrendingUp size={11} /> : <TrendingDown size={11} />}
            {delta > 0 ? '+' : ''}
            {delta} pp vs periodo ant. ({checkpointLabel})
        </span>
    );
}

function formatStageCell(point: LeanFunnelCheckpoint | undefined, stage: LeanFunnelStageKey): string {
    if (!point || point.stages[stage] == null) return 'n/d';
    const pct = point.stages[stage];
    const n = point.counts?.[stage];
    if (typeof n === 'number') return `${pct}% (${n})`;
    return `${pct}%`;
}

export function ProductLeanFunnelPanel() {
    const { data, isLoading, isError, error, refetch, isFetching } = useProductLeanFunnel();
    const [mode, setMode] = useState<SeriesMode>('current');

    const chartData = useMemo(() => {
        if (!data) return [];
        return toChartRows(mode === 'current' ? data.current : data.previous);
    }, [data, mode]);

    useEffect(() => {
        if (!data) return;
        if (data.current.length === 0 && data.previous.length > 0) {
            setMode('previous');
        }
    }, [data]);

    if (isLoading && !data) {
        return (
            <div className="rounded-xl border border-border bg-card p-8 flex flex-col items-center gap-2">
                <RefreshCw size={20} className="animate-spin text-muted-foreground" />
                <p className="text-xs text-muted-foreground">Calculando embudo Lean (puede tardar unos segundos)…</p>
            </div>
        );
    }

    if (isError) {
        return (
            <div className="rounded-xl border border-rose-500/30 bg-rose-500/5 p-5 space-y-2">
                <p className="text-sm font-semibold text-rose-400">No se pudo cargar el embudo Lean</p>
                <p className="text-xs text-muted-foreground">{(error as Error)?.message}</p>
                <button
                    type="button"
                    onClick={() => refetch()}
                    className="text-xs text-primary hover:underline inline-flex items-center gap-1"
                >
                    <RefreshCw size={12} className={isFetching ? 'animate-spin' : ''} />
                    Reintentar
                </button>
            </div>
        );
    }

    if (!data || (data.current.length === 0 && data.previous.length === 0)) {
        return (
            <div className="rounded-xl border border-dashed border-border bg-card/50 p-8 text-center text-sm text-muted-foreground">
                No hay cohortes suficientes para el embudo Lean
            </div>
        );
    }

    const comparable = lastComparableCheckpoint(data.current, data.previous);
    const comparableLabel = comparable?.label ?? 'último checkpoint común';
    const windowMeta = mode === 'current' ? data.current_window : data.previous_window;
    const seriesEmpty = chartData.length === 0;
    const currentMissingW10 = !data.current.some((c) => c.key === 'W10');

    return (
        <div className="rounded-xl border border-border bg-card p-5 space-y-4">
            <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="space-y-1 min-w-0">
                    <h3 className="text-sm font-semibold text-foreground">
                        Embudo Lean — distribución por edad del usuario
                    </h3>
                    <p className="text-xs text-muted-foreground max-w-2xl">
                        Cada columna suma 100%: cada usuario cuenta solo en su etapa más alta a esa edad.
                        El eje es la edad del usuario (7 días, luego semanas 2–10), no el calendario.
                        La capa clara («Registrados sin sesión») es gente que aún no abrió la app.
                    </p>
                    <div className="flex flex-wrap items-center gap-3 pt-1">
                        <StageDelta
                            current={data.current}
                            previous={data.previous}
                            stage="still_active"
                            checkpointLabel={comparableLabel}
                        />
                        <StageDelta
                            current={data.current}
                            previous={data.previous}
                            stage="no_session"
                            checkpointLabel={comparableLabel}
                        />
                    </div>
                </div>
                <div className="flex items-center gap-2">
                    <div className="flex gap-1 p-1 bg-muted/50 rounded-lg">
                        <button
                            type="button"
                            onClick={() => setMode('current')}
                            className={cn(
                                'px-3 py-1.5 rounded-md text-xs font-medium transition-all',
                                mode === 'current'
                                    ? 'bg-primary text-primary-foreground shadow-sm'
                                    : 'text-muted-foreground hover:text-foreground'
                            )}
                        >
                            Últimas 10 sem. ({data.current_window.cohort_n})
                        </button>
                        <button
                            type="button"
                            onClick={() => setMode('previous')}
                            className={cn(
                                'px-3 py-1.5 rounded-md text-xs font-medium transition-all',
                                mode === 'previous'
                                    ? 'bg-primary text-primary-foreground shadow-sm'
                                    : 'text-muted-foreground hover:text-foreground'
                            )}
                        >
                            10 sem. ant. ({data.previous_window.cohort_n})
                        </button>
                    </div>
                    <button
                        type="button"
                        onClick={() => refetch()}
                        className="p-2 rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted/50"
                        title="Actualizar"
                    >
                        <RefreshCw size={14} className={isFetching ? 'animate-spin' : ''} />
                    </button>
                </div>
            </div>

            <div className="flex gap-2 rounded-lg border border-border/60 bg-muted/20 px-3 py-2 text-[11px] text-muted-foreground">
                <Info size={14} className="shrink-0 text-primary mt-0.5" />
                <span>
                    Mostrando <strong className="text-foreground">{windowMeta.label}</strong>
                    {' · '}
                    {windowMeta.cohort_n.toLocaleString()} registros
                    {' · '}
                    En cada punto solo entran quienes ya tienen esa edad. Por eso «Últimas 10 sem.»
                    no llega a Semana 10: esa gente aún no tiene 70 días.
                    {currentMissingW10 && mode === 'current' ? (
                        <> La tabla compara en <strong className="text-foreground">{comparableLabel}</strong>, no en Semana 10.</>
                    ) : null}
                    {' '}
                    <strong className="text-foreground">Siguen activos</strong>: sesión esa semana y además 1ª captura o retención.
                </span>
            </div>

            <div className="h-[340px] w-full relative">
                {seriesEmpty ? (
                    <div className="absolute inset-0 flex items-center justify-center text-sm text-muted-foreground px-6 text-center">
                        Esta cohorte aún no tiene usuarios con 7 días de vida. Cambia a «10 sem. ant.» para ver el embudo completo.
                    </div>
                ) : null}
                <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={chartData} margin={{ top: 8, right: 12, left: 0, bottom: 0 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="hsl(222 30% 16%)" />
                        <XAxis
                            dataKey="label"
                            tick={{ fill: 'hsl(215 20% 65%)', fontSize: 11 }}
                            axisLine={false}
                            tickLine={false}
                        />
                        <YAxis
                            domain={[0, 100]}
                            tickFormatter={(v) => `${v}%`}
                            tick={{ fill: 'hsl(215 20% 65%)', fontSize: 11 }}
                            axisLine={false}
                            tickLine={false}
                            width={40}
                        />
                        <Tooltip
                            contentStyle={{
                                background: 'hsl(224 39% 15%)',
                                border: '1px solid hsl(222 30% 22%)',
                                borderRadius: 8,
                                fontSize: 12,
                            }}
                            formatter={(value: number, name: string) => {
                                const label = data.stage_labels[name as LeanFunnelStageKey] ?? name;
                                return [`${value}%`, label];
                            }}
                            labelFormatter={(label, payload) => {
                                const n = payload?.[0]?.payload?.cohort_n;
                                return n != null ? `${label} · n=${n}` : String(label);
                            }}
                        />
                        <Legend
                            wrapperStyle={{ fontSize: 11, paddingTop: 8 }}
                            formatter={(value) => data.stage_labels[value as LeanFunnelStageKey] ?? value}
                        />
                        {[...STACK_TOP_TO_BOTTOM].reverse().map((stage) => (
                            <Area
                                key={stage}
                                type="linear"
                                dataKey={stage}
                                stackId="lean"
                                stroke={STAGE_COLORS[stage]}
                                fill={STAGE_COLORS[stage]}
                                fillOpacity={STAGE_FILL_OPACITY[stage]}
                                strokeWidth={1}
                                isAnimationActive={false}
                            />
                        ))}
                    </AreaChart>
                </ResponsiveContainer>
            </div>

            <div className="space-y-2">
                <div>
                    <h4 className="text-xs font-semibold text-foreground">
                        ¿En qué etapa están a los {comparable?.age_days ?? '—'} días ({comparableLabel})?
                    </h4>
                    <p className="text-[11px] text-muted-foreground mt-0.5 max-w-3xl">
                        Comparamos dos grupos de registros: los de las últimas 10 semanas frente a los de las 10 anteriores,
                        en el último punto de edad que ambos ya han cumplido.
                        Cada fila es el % de ese grupo (entre paréntesis, personas). Las etapas son excluyentes:
                        un usuario Pro no se cuenta también en «1ª captura».
                    </p>
                </div>
                <div className="overflow-x-auto">
                    <table className="w-full text-xs">
                        <thead>
                            <tr className="text-muted-foreground border-b border-border">
                                <th className="text-left py-2 font-medium">Etapa</th>
                                <th className="text-right py-2 font-medium">Últimas 10 sem.</th>
                                <th className="text-right py-2 font-medium">10 sem. ant.</th>
                                <th className="text-right py-2 font-medium">Δ</th>
                            </tr>
                        </thead>
                        <tbody>
                            {STACK_TOP_TO_BOTTOM.map((stage) => {
                                const curPoint = comparable
                                    ? data.current.find((c) => c.key === comparable.key)
                                    : undefined;
                                const prevPoint = comparable
                                    ? data.previous.find((c) => c.key === comparable.key)
                                    : undefined;
                                const cur = curPoint?.stages[stage];
                                const prev = prevPoint?.stages[stage];
                                const hasDelta = cur != null && prev != null;
                                const delta = hasDelta ? Number((cur - prev).toFixed(1)) : null;
                                const positiveIsGood = stage !== 'no_session';
                                const good = delta == null ? true : (positiveIsGood ? delta >= 0 : delta <= 0);
                                return (
                                    <tr key={stage} className="border-b border-border/40">
                                        <td className="py-1.5">
                                            <span className="inline-flex items-center gap-2">
                                                <span
                                                    className="w-2.5 h-2.5 rounded-sm shrink-0"
                                                    style={{ background: STAGE_COLORS[stage] }}
                                                />
                                                {data.stage_labels[stage]}
                                            </span>
                                        </td>
                                        <td className="py-1.5 text-right tabular-nums">
                                            {formatStageCell(curPoint, stage)}
                                        </td>
                                        <td className="py-1.5 text-right tabular-nums text-muted-foreground">
                                            {formatStageCell(prevPoint, stage)}
                                        </td>
                                        <td
                                            className={cn(
                                                'py-1.5 text-right tabular-nums font-semibold',
                                                delta == null
                                                    ? 'text-muted-foreground'
                                                    : good ? 'text-emerald-400' : 'text-rose-400'
                                            )}
                                        >
                                            {delta == null
                                                ? 'n/d'
                                                : `${delta > 0 ? '+' : ''}${delta} pp`}
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
}
